import {Box, LinearProgress} from '@mui/material';
import {WidgetPlaceholder} from 'main/utility/DebugUtil';
import {usePrevious} from 'main/utility/hooks';
import {useWidget} from 'main/utility/hooks/useWidget';
import PropTypes from 'prop-types';
import {useEffect, useMemo, useState} from 'react';
import {useDispatch} from 'react-redux';
import {useLocation, useParams} from 'react-router-dom';
import {
  WIDGET_RESET_DATA,
  WIDGET_RESET_STATE,
} from 'shared/constants/ActionTypes';
import {
  UiWiseWidgetState,
  UiWiseWidgetType,
} from 'shared/constants/UiWiseConst';
import DocInvoiceWidget from './DocInvoiceWidget';
import FormWidget from './FormWidget';
import TableWidget from './TableWidget';
import TimelineWidget from './TimelineWidget';
import {WidgetSkeleton} from './WidgetSkeleton';

const renderWidget = (widgetSpec, isSingleWidget, params) => {
  switch (widgetSpec.widgettype) {
    case UiWiseWidgetType.TABLE_LIST:
    case UiWiseWidgetType.TABLE_GRID:
      return (
        <TableWidget
          widgetSpec={widgetSpec}
          isSingleWidget={isSingleWidget}
          params={params}
        />
      );

    case UiWiseWidgetType.FORM:
      return <FormWidget widgetSpec={widgetSpec} params={params} />;

    case UiWiseWidgetType.TIMELINE:
      return <TimelineWidget widgetSpec={widgetSpec} params={params} />;

    case UiWiseWidgetType.DOC_INVOICE:
      return <DocInvoiceWidget widgetSpec={widgetSpec} params={params} />;

    case UiWiseWidgetType.ALERT:
      return null;

    default:
      return (
        <WidgetPlaceholder
          widgetSpec={widgetSpec}
          isSingleWidget={isSingleWidget}
          params={params}
        />
      );
  }
};

const UiWiseWidget = ({widgetSpec, isSingleWidget}) => {
  const dispatch = useDispatch();
  const pathParams = useParams();
  const {search} = useLocation();

  const currentParams = useMemo(
    () => ({
      ...Object.fromEntries(new URLSearchParams(search)),
      ...pathParams,
    }),
    [search, pathParams],
  );
  const paramsString = JSON.stringify(currentParams);

  const {widgetid, widgettype} = widgetSpec;
  const {widgetState, isInit, isLoading} = useWidget(widgetid);
  const prevState = usePrevious(widgetState);

  const [debouncedLoading, setDebouncedLoading] = useState(false);

  useEffect(() => {
    let timer;
    if (isLoading || isInit) {
      // If we are loading, wait 200ms before showing any loading UI
      timer = setTimeout(() => {
        setDebouncedLoading(true);
      }, 200);
    } else {
      // If we aren't loading, turn it off immediately
      setDebouncedLoading(false);
    }

    return () => clearTimeout(timer); // Cleanup timer if state changes fast
  }, [isLoading, isInit]);

  const isTable =
    widgettype === UiWiseWidgetType.TABLE_LIST ||
    widgettype === UiWiseWidgetType.TABLE_GRID;

  useEffect(() => {
    // console.log(`🟢 MOUNT WIDGET [${widgetid}]`);

    return () => {
      // console.log(`🔴 UNMOUNT WIDGET [${widgetid}]`);

      // Clean up the state to avoid Tables going empty and rerendering
      dispatch({type: WIDGET_RESET_STATE, widgetId: widgetid});
      dispatch({type: WIDGET_RESET_DATA, widgetId: widgetid});
    };
  }, [widgetid, paramsString, widgettype]);

  const showSkeleton =
    debouncedLoading && (isInit || prevState === UiWiseWidgetState.INIT);
  const showProgressBar =
    debouncedLoading && prevState !== UiWiseWidgetState.INIT && isLoading;

  return (
    <Box sx={{position: 'relative', height: '100%'}}>
      {showSkeleton && <WidgetSkeleton isTable={isTable} />}
      {showProgressBar && (
        <LinearProgress
          sx={{position: 'absolute', top: 0, width: '100%', zIndex: 1000}}
        />
      )}
      {renderWidget(widgetSpec, isSingleWidget, currentParams)}
    </Box>
  );
};

export default UiWiseWidget;

UiWiseWidget.propTypes = {
  widgetSpec: PropTypes.object.isRequired,
  isSingleWidget: PropTypes.bool.isRequired,
};
